import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Sun, Moon, Monitor, LogOut } from 'lucide-react';
import { getUser, clearAuth } from '../../utils/auth';
import { useTheme } from '../../context/ThemeContext';
import { useBranding } from '../../contexts/BrandingContext';
import { toast } from 'sonner';
import PlatformLogo from '../shared/PlatformLogo';
import ECEWordmark from '../shared/ECEWordmark';

/**
 * Mobile Header Component
 *
 * Tenant-aware header with dynamic branding from BrandingContext.
 * Structure:
 * - Left: Platform logo + Wordmark + Tagline (stacked)
 * - Right: Bell notification icon + User avatar
 *
 * CSS:
 * - Background: Platform surface color with backdrop blur
 * - Border: Border color from design tokens
 * - Padding: safe-area top, 13px bottom, 18px horizontal
 */
const MobileHeader = ({
  id = 'topbar',
  showNotifications = true,
  notificationCount = 0,
  onNotificationClick,
}) => {
  const navigate = useNavigate();
  const user = getUser();
  const { themePreference, setTheme } = useTheme();
  const branding = useBranding();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    clearAuth();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const themeOptions = [
    { value: 'light', label: 'Light', icon: Sun },
    { value: 'dark', label: 'Dark', icon: Moon },
    { value: 'system', label: 'System', icon: Monitor },
  ];

  // Get initials for avatar
  const getInitials = () => {
    if (user?.name) {
      const parts = user.name.split(' ');
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      }
      return user.name[0].toUpperCase();
    }
    return user?.email?.[0]?.toUpperCase() || 'U';
  };

  return (
    <header
      className="
        md:hidden sticky top-0 z-[100]
        bg-el-pbg dark:bg-[rgba(6,13,24,0.85)]
        border-b border-[rgba(10,22,40,0.08)] dark:border-[rgba(255,255,255,0.07)]
        pt-[env(safe-area-inset-top,0px)] pb-[13px] px-[18px]
        flex items-center justify-between
        backdrop-blur-[20px]
      "
    >
      {/* Left: Brand - ECEWordmark only (no duplicate PlatformLogo) */}
      <div className="flex items-center">
        <ECEWordmark showTagline size="sm" />
      </div>

      {/* Right: Actions */}
      <div className="flex items-center gap-2">
        {/* Bell notification icon */}
        {showNotifications && (
          <button
            onClick={onNotificationClick}
            className="
              w-8 h-8
              bg-ece-surface2 dark:bg-[#131F30]
              border border-[rgba(10,22,40,0.08)] dark:border-[rgba(255,255,255,0.07)]
              rounded-full
              flex items-center justify-center
              text-ece-text2 dark:text-[#7A95AE]
              hover:bg-ece-surface3 dark:hover:bg-[#1A2A3C]
              hover:border-[rgba(10,22,40,0.14)] dark:hover:border-[rgba(255,255,255,0.12)]
              transition-all duration-150
              relative
            "
            aria-label="Notifications"
          >
            <Bell size={14} />
            {notificationCount > 0 && (
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500
                               text-white text-[9px] font-bold rounded-full
                               flex items-center justify-center">
                {notificationCount > 9 ? '9+' : notificationCount}
              </span>
            )}
          </button>
        )}

        {/* User avatar with Dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            style={{ backgroundColor: branding.primary_color }}
            className="
              w-8 h-8
              rounded-full
              flex items-center justify-center
              font-syne text-[11px] font-bold
              text-white
              tracking-[0.02em]
            "
            aria-label="Profile"
            aria-expanded={isDropdownOpen}
          >
            {getInitials()}
          </button>

          {/* Dropdown Menu */}
          {isDropdownOpen && (
            <div className="absolute right-0 top-full mt-2 w-56 p-3
                          bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                          rounded-xl shadow-lg z-50">
              {/* User Info */}
              <div className="pb-3 border-b border-slate-200 dark:border-slate-700">
                <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{user?.name || 'User'}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{user?.email}</p>
              </div>

              {/* Theme Toggle */}
              <div className="py-3 border-b border-slate-200 dark:border-slate-700">
                <p className="text-[10px] uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-2">Theme</p>
                <div className="flex gap-1">
                  {themeOptions.map((option) => {
                    const Icon = option.icon;
                    const isActive = themePreference === option.value;
                    return (
                      <button
                        key={option.value}
                        onClick={() => setTheme(option.value)}
                        style={isActive ? {
                          backgroundColor: `${branding.primary_color}20`,
                          color: branding.primary_color
                        } : {}}
                        className={`flex-1 flex flex-col items-center gap-1 py-2 rounded-lg transition-colors ${
                          isActive
                            ? ''
                            : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        <span className="text-[10px]">{option.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Logout */}
              <button
                onClick={handleLogout}
                className="w-full mt-3 flex items-center gap-2 px-3 py-2.5
                         text-red-500 dark:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span className="text-sm">Logout</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default MobileHeader;
